import React from "react";
import PropTypes from "prop-types";
import { QuestionWrapper } from "@learningblocks/storybook-components/lib/Questions/Components/QuestionWrapper";
import { MainTitle } from "@learningblocks/storybook-components/lib/Questions/Components/MainTitle";
import { Question } from "@learningblocks/storybook-components/lib/Questions/Components/Question";
import { Footer } from "@learningblocks/storybook-components/lib/Questions/Components/Footer";
import { TotalAttempts } from "@learningblocks/storybook-components/lib/Questions/Components/TotalAttempts";
import { SubmitButton } from "@learningblocks/storybook-components/lib/Questions/Components/SubmitButton";
import { HintButton } from "@learningblocks/storybook-components/lib/Questions/Components/HintButton";
import { HintArea } from "@learningblocks/storybook-components/lib/Questions/Components/HintArea";
import { Image } from "@learningblocks/shared-components";
import ALERT_BALANCED from "@common/images/alert_balanced.svg";
import ALERT_UNBALANCED from "@common/images/alert_unbalanced.svg";
import { DataTable, LogEvent } from "@learningblocks/shared-components";

import "./style.scss";

/**
+ * Deep-clone plain data on Safari 13 (fallback to JSON for simple objects/arrays)
+ */
function deepClone(obj) {
  return typeof structuredClone === "function"
    ? structuredClone(obj)
    : JSON.parse(JSON.stringify(obj));
}

export function QuestionTable(props) {
  const onSubmit = () => {
    // Reset hint state when submitting a new set of answers.
    if (props.showHint) {
      props.setShowHint({
        componentID: props.id,
        showHint: false,
      });
    }

    props.submitAnswer(props.id, props.type);
  };
  const onCellChange = (params) => {
    // Run the cell change action.
    props.onCellChange({
      componentID: props.id,
      ...params,
    });
  };

  const concatHints = () => {
    return props?.hints?.slice(0, props?.hintIndex + 1).map((hint) => {
      const hintParams = deepClone(hint);
      if (props.titleRows) hintParams.rowId -= props.titleRows;

      return (
        <p>{`row: ${hintParams.rowId}, col: ${hintParams.colId}, feedback: ${hintParams.feedback}`}</p>
      );
    });
  };

  const getCorrectFeedback = () => {
    const defaultFeedback = "Correct!";
    if (!props.correctFeedback) return defaultFeedback;
    const data =
      props.data[props.correctFeedback.rowId]?.[props.correctFeedback.colId];
    if (!data) return defaultFeedback;
    return (data.correctFeedback || defaultFeedback).replace(
      "[ANSWER]",
      data.answer?.[0] ?? data.answer
    );
  };

  const getIncorrectFeedback = () => {
    const defaultFeedback = "Not Quite!";
    if (!props.incorrectFeedback) return defaultFeedback;
    const data =
      props.data[props.incorrectFeedback.rowId]?.[
        props.incorrectFeedback.colId
      ];
    if (!data) return defaultFeedback;
    return (data.incorrectFeedback || defaultFeedback).replace(
      "[ANSWER]",
      data.answer?.[0] ?? data.answer
    );
  };

  return (
    <QuestionWrapper className="questionTable">
      <MainTitle className="questionTable__mainTitle" title={props.title} />
      <Question text={props.question} className="questionTable__question" />
      <Question text={props.text} className="questionTable__question" />
      <DataTable
        id={props.id}
        className={"questionTable__tableWrapper"}
        indices={props.indices}
        data={props.data}
        onCellChange={onCellChange}
      />
      <HintArea showHint={props.showHint} hint={concatHints()} />
      {props.correct === true || props.correct === false ? (
        <div
          className={`correctStatus ${props.correct}`}
          style={{ marginTop: "16px" }}
        >
          {props.correct ? (
            <div
              style={{
                borderRadius: "5px",
                border: "1px solid #84BE60",
                background: "#F3F8EF",
                padding: "8px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Image src={ALERT_BALANCED} alt="" />
              <span style={{ marginLeft: "8px" }}>{getCorrectFeedback()}</span>
            </div>
          ) : (
            <div
              style={{
                borderRadius: "5px",
                border: "1px solid #ED3431",
                background: "#FDEBEA",
                padding: "8px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Image src={ALERT_UNBALANCED} alt="" />
              <span style={{ marginLeft: "8px" }}>
                {getIncorrectFeedback()}
              </span>
            </div>
          )}
        </div>
      ) : null}
      <Footer className="questionTable__footer">
        <TotalAttempts
          totalAttempts={
            props.totalAttempts == -1 ? "unlimited" : props.totalAttempts
          }
          className="questionTable__footer__totalAttempts"
        />
        {props?.hints?.length > props?.hintIndex + 1 ||
        (props?.hints?.length === props?.hintIndex + 1 && !props.showHint) ? (
          <HintButton
            disabled={false}
            firstHint={props?.showHint ? false : true}
            showHint={() => {
              if (!props.showHint) {
                props.setShowHint({
                  componentID: props.id,
                  showHint: true,
                });
              } else {
                props.showNextHint({
                  componentID: props.id,
                });
              }
            }}
          />
        ) : null}
        <LogEvent
          logEventProps={{
            event_type: "sim_numericalquestion submitted",
            event_schema: "checksubmission",
            question_number: props.id,
            correct: props.correct,
          }}
          actionProps={{ onSubmit: { action: "submit" } }}
          elementType="button"
        >
          <SubmitButton disabled={props.submitDisabled} onSubmit={onSubmit} />
        </LogEvent>
      </Footer>
    </QuestionWrapper>
  );
}

QuestionTable.propTypes = {
  /**
   * Question for the component
   */
  question: PropTypes.string,
  /**
   * Number of [rows, columns] in the table
   */
  indices: PropTypes.arrayOf(PropTypes.number),
  /**
   * Data to be rendered in the table
   */
  data: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * The type of the cell
         */
        type: PropTypes.oneOf(["TITLE", "FIXED", "INPUT", "DROPDOWN"]),
        /**
         * Text to render in cell [ TITLE, FIXED]
         */
        text: PropTypes.text,
        /**
         * Type of the input [ INPUT ]
         */
        inputType: PropTypes.oneOf(["number", "text"]),
        /**
         * placeholder of for cell [ DROPDOWN, INPUT]
         */
        placeholder: PropTypes.string,
        /**
         * options to display on dropdown [DROPDOWN]
         */
        options: PropTypes.array,
        /**
         * Boolean to disable the cell
         */
        disabled: PropTypes.bool,
      })
    )
  ),
  /**
   * Function to invoke when the data in a cell changes
   */
  onCellChange: PropTypes.func,
  /**
   * Boolean to set if question is correct
   */
  correct: PropTypes.bool,
  /**
   * UniqueId for the question component
   */
  id: PropTypes.number,
  /**
   * Total number of  attempts to solve question
   */
  totalAttempts: PropTypes.number,
  /**
   * Function to invoke on submit button click
   */
  submitAnswer: PropTypes.func,
  /**
   * Boolean to set submit button enabled/disabled
   */
  submitDisabled: PropTypes.bool,
};
